// @flow
import DataUtils from 'common/utils/DataUtils';
import { LocusApiModel } from 'common/modules/api';
import Enum from 'common/enums/Enum';
import DOMUtils from 'common/utils/DOMUtils';

const REQUEST_STATE = new Enum({
    IDLE: 'IDLE',
    POLLING: 'POLLING',
    FAILED: 'FAILED',
    TERMINATED: 'TERMINATED'
});

class RequestStateModel {
    requestMap: Object;
    constructor() {
        this.requestMap = {};
        this.requestPromise = {};
    }

    setRequestPromise = (key, promise) => {
        if (!promise) return;
        this.requestPromise[key] = promise;
    };

    getRequestPromise = key => {
        return this.requestPromise[key];
    };

    deleteWhenDone(key, promise) {
        Promise.resolve(promise)
            .then(() => {
                delete this.requestPromise[key];
            })
            .catch(() => {
                delete this.requestPromise[key];
            });
    }

    setStateToIdle = key => {
        if (!key) return;
        this.requestMap[key] = REQUEST_STATE.IDLE;
    };
    setStateToPolling = key => {
        if (!key) return;
        this.requestMap[key] = REQUEST_STATE.POLLING;
    };
    setStateToFailed = key => {
        if (!key) return;
        this.requestMap[key] = REQUEST_STATE.FAILED;
    };
    setStateToTerminated = key => {
        if (!key) return;
        this.requestMap[key] = REQUEST_STATE.TERMINATED;
    };

    getter = key => this.requestMap[key];
}

// 5 SECONDS DEFAULT TIMER
const DefaultTimeout = 5000;
const DisablePollingIfTabInactive = true;
const AutoTerminateOn4xx = true;
const requestStateModel = new RequestStateModel();

const terminateOnErrorInterceptor = (request: Object, response: Object) => {
    return new Promise((resolve: Function) => {
        const url = `${request.options.method}${request.url}`;
        if (!response.ok && requestStateModel.getter(url)) {
            requestStateModel.setStateToFailed(url);
        }
        resolve(response);
    });
};
export default class ControlledSetInterval {
    targetCallback: Function;
    timeout: number;
    autoTerminateOn4xx: ?boolean;
    interval: ?IntervalID;
    apiModel: LocusApiModel;
    onSuccessCallback: ?Function;
    onFailureCallback: ?Function;
    apiURL: ?string;
    disablePollingIfTabInactive: boolean;

    constructor() {
        this.setDefaults();
    }

    setDefaults = () => {
        this.targetCallback = null;
        this.apiModel = null;
        this.apiURL = null;
        this.interval = null;
        this.timeout = DefaultTimeout;
        this.disablePollingIfTabInactive = DisablePollingIfTabInactive;
        this.autoTerminateOn4xx = AutoTerminateOn4xx;
        this.onSuccessCallback = null;
        this.onFailureCallback = null;
    };

    applyInterceptors = () => {
        if (this.autoTerminateOn4xx && this.apiModel) {
            this.apiModel.setInterceptor(terminateOnErrorInterceptor, '404');

            this.apiModel.setInterceptor(terminateOnErrorInterceptor, '400');
        }
    };

    terminatePolling() {
        requestStateModel.setStateToTerminated(this.apiURL);
        this.stopPolling();
    }

    stopPolling() {
        if (this.interval) {
            clearInterval(this.interval);
        }
    }

    checkIfSameRequestInProgress = () => {
        if (!this.apiURL) {
            const promise = this.targetCallback();
            return promise;
        }
        const existingPromise = requestStateModel.getRequestPromise(
            this.apiURL
        );
        if (existingPromise) {
            return existingPromise;
        } else {
            const promise = this.targetCallback();
            requestStateModel.setRequestPromise(this.apiURL, promise);
            requestStateModel.deleteWhenDone(this.apiURL, promise);

            return promise;
        }
    };

    onTargetCallback() {
        const promise = this.checkIfSameRequestInProgress();

        /**
         * Handling in case the fetch call has a .then/.catch method to be invoked on container
         * This pattern will make sure recurring invoke of the callback on container.
         */
        Promise.resolve(promise)
            .then(result => {
                this.onSuccessCallback && this.onSuccessCallback(result);
            })
            .catch(err => {
                this.onFailureCallback && this.onFailureCallback(err);
            });
    }

    startPolling() {
        this.stopPolling();

        requestStateModel.setStateToPolling(this.apiURL);

        this.interval = setInterval(() => {
            if (this.apiURL && this.autoTerminateOn4xx) {
                /**
                 * Pausing Poller when tab is inactive or document is still rendering
                 * https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilityState
                 */
                const isTabInactive = DOMUtils.isCurrentTabInactive();
                const state = requestStateModel.getter(this.apiURL);

                const isFailed = state === REQUEST_STATE.FAILED;
                const isTerminated = state === REQUEST_STATE.TERMINATED;
                const isIdle = state === REQUEST_STATE.IDLE;

                if (
                    isTerminated ||
                    (isTabInactive && this.disablePollingIfTabInactive)
                ) {
                    return;
                }

                if (isFailed) {
                    // stop polling if api requested returned 4xx.
                    this.stopPolling();
                    return;
                }

                if (isIdle) {
                    requestStateModel.setStateToPolling(this.apiURL);
                }
            }

            this.onTargetCallback();
        }, this.timeout);
    }

    setAPIModel({
        apiModel,
        apiMethod = 'GET',
        params,
        autoTerminateOn4xx,
        disablePollingIfTabInactive
    }: {
        apiModel?: LocusApiModel,
        params?: Object,
        autoTerminateOn4xx?: boolean,
        disablePollingIfTabInactive?: boolean
    }) {
        if (DataUtils.isBoolean(disablePollingIfTabInactive)) {
            this.disablePollingIfTabInactive = disablePollingIfTabInactive;
        }
        if (DataUtils.isBoolean(autoTerminateOn4xx)) {
            this.autoTerminateOn4xx = autoTerminateOn4xx;
        }

        if (this.autoTerminateOn4xx) {
            if (!apiModel && !this.apiModel) {
                throw new Error(
                    'Please provide a valid Api model to setup auto terminate on any 4xx response'
                );
            }

            this.apiModel = apiModel || this.apiModel;
            this.apiURL = params && this.apiModel.getUrl(params);
            if (apiMethod) {
                this.apiURL = apiMethod + this.apiURL;
            }
        }
    }

    setPollerState = ({
        apiModel,
        apiMethod,
        params,
        autoTerminateOn4xx,
        disablePollingIfTabInactive
    }: {
        apiModel?: LocusApiModel,
        params?: Object,
        autoTerminateOn4xx?: boolean,
        disablePollingIfTabInactive?: boolean
    }) => {
        this.setAPIModel({
            autoTerminateOn4xx,
            apiModel,
            apiMethod,
            params,
            disablePollingIfTabInactive
        });

        if (!(this.targetCallback instanceof Function)) {
            throw new Error(
                `Polling method missing on ControlledSetInterval, expected function found ${typeof this
                    .targetCallback}`
            );
        }

        requestStateModel.setStateToIdle(this.apiURL);
        this.applyInterceptors();
    };

    configurePoller({
        apiModel,
        apiMethod,
        params,
        callback,
        timeout,
        autoTerminateOn4xx,
        disablePollingIfTabInactive,
        onSuccessCallback,
        onFailureCallback
    }: {
        apiModel?: LocusApiModel,
        params?: Object,
        callback: Function,
        timeout?: number,
        autoTerminateOn4xx?: boolean,
        disablePollingIfTabInactive?: boolean,
        onSuccessCallback?: Function,
        onFailureCallback?: Function
    }) {
        this.setDefaults();

        this.setAPIModel({
            autoTerminateOn4xx,
            apiModel,
            apiMethod,
            params,
            disablePollingIfTabInactive
        });

        if (!(callback instanceof Function)) {
            throw new Error(
                `Please provide a valid method to poll data from api, not ${typeof callback}`
            );
        }

        this.targetCallback = callback;
        this.onSuccessCallback = onSuccessCallback;
        this.onFailureCallback = onFailureCallback;
        if (!DataUtils.isEmpty(timeout)) {
            this.timeout = timeout;
        }
        requestStateModel.setStateToIdle(this.apiURL);

        this.applyInterceptors();

        /**
         * if only ControlledSetInterval.init is invoked we return the instance of ControlledSetInterval class.
         * This will help user to control the startPolling and stopPolling on their end.
         */
        return this;
    }

    /**
     * This initiates a stateless polling, ie. the poller is not configurable from outside the class
     * Use case - poll loader, stateless api call (api params don't change)
     * @param {apiModel, params, callback, timeout, autoTerminateOn4xx, onSuccessCallback, onFailureCallback}
     * returns Callback to Terminate Polling
     */
    initiateStatelessPolling({
        apiModel,
        apiMethod,
        params,
        callback,
        timeout,
        autoTerminateOn4xx,
        disablePollingIfTabInactive,
        onSuccessCallback,
        onFailureCallback
    }: {
        apiModel?: LocusApiModel,
        params?: Object,
        callback: Function,
        timeout?: number,
        autoTerminateOn4xx?: boolean,
        disablePollingIfTabInactive?: boolean,
        onSuccessCallback?: Function,
        onFailureCallback?: Function
    }) {
        this.configurePoller({
            apiModel,
            apiMethod,
            params,
            callback,
            timeout,
            autoTerminateOn4xx,
            disablePollingIfTabInactive,
            onSuccessCallback,
            onFailureCallback
        });

        this.startPolling();
        /**
         * If the user wishes to terminate polling on their end
         * Incase on ComponentWillUnmount explicit clearInterval allowed
         * Use ControlledSetInterval.stopInterval(interval);
         */
        return this.terminatePolling.bind(this);
    }
}
